import {
  TypedDocumentNode,
  gql,
  ApolloClient,
  NormalizedCacheObject,
  ApolloError,
  ApolloQueryResult,
} from '@apollo/client';
import {
  seoSegmentationListingPage as SeoSegmentationListingPageType,
  seoSegmentationListingPageVariables,
} from '@/__generated__/seoSegmentationListingPage';
import {
  CAREGIVER_SEARCH_RESULT_FIELDS,
  PROVIDER_SEARCH_RESULT_FIELDS,
  JOB_SEARCH_RESULT_FIELDS,
  ARCHIVED_JOB_SEARCH_RESULT_FIELDS,
  ACTIVITY_SEARCH_RESULT_FIELDS,
} from './fragments/common';
import { apolloClientQuery } from './utils';

export const SEO_SEGMENTATION_LISTING_PAGE: TypedDocumentNode<
  SeoSegmentationListingPageType,
  seoSegmentationListingPageVariables
> = gql`
  ${CAREGIVER_SEARCH_RESULT_FIELDS}
  ${PROVIDER_SEARCH_RESULT_FIELDS}
  ${JOB_SEARCH_RESULT_FIELDS}
  ${ARCHIVED_JOB_SEARCH_RESULT_FIELDS}
  ${ACTIVITY_SEARCH_RESULT_FIELDS}
  query seoSegmentationListingPage(
    $seoSegmentId: ID!
    $pageNumber: Int
    $filters: SEOSegmentationFilterInput
    $ipAddress: String!
    $userGeoInfo: String!
    $botInfo: String!
    $userAgent: String!
    $numberOfReviewsPerProfile: Int
    $local: Boolean!
    $locationId: ID!
    $isJobSegment: Boolean!
  ) {
    seoSegmentationSearchResults(
      filters: $filters
      pageNumber: $pageNumber
      testCell: null
      seoSegmentId: $seoSegmentId
      ipAddress: $ipAddress
      botInfo: $botInfo
      userGeoInfo: $userGeoInfo
      userAgent: $userAgent
      numberOfReviewsPerProfile: $numberOfReviewsPerProfile
    ) {
      ... on SEOSegmentationSearchResultsSuccess {
        ...CaregiverSearchResultFields
        ...ProviderSearchResultFields
        ...JobSearchResultFields
        ...ArchivedJobSearchResultFields
        ...ActivitySearchResultFields
        tests {
          testName
          testVariant
        }
        filters {
          location {
            city
            state
            zipcode
          }
        }
      }
      ... on SEOSegmentationSearchResultsError {
        errors {
          __typename
          message
        }
      }
    }
    seoSegmentationDetails(seoSegmentId: $seoSegmentId) {
      __typename
      ... on SEOSegmentationDetailsSuccess {
        segmentationDetails {
          seoPageMetadata {
            title
            metaKeywords
            indexable
            metaDescriptions
            canonicalURL
            seoPageId
            hrefLangLinks {
              countryCode
              languageCode
              url
            }
          }
          headline
          subHeadline
          conversionHeader
          searchResultsHeader
          searchFilters
          searchingFor
          pageType
          smbHeadline
          crossSell {
            avgPayRates {
              title
              url
              avgPayRate {
                rate {
                  amount
                  currencyCode
                }
                rateDuration
              }
            }
          }
          city
          state
          segmentationSubService
          serviceId
          serviceIdForMember
          avgPayRate {
            rate {
              amount
              currencyCode
            }
            rateDuration
          }
          minPayRate {
            rate {
              amount
              currencyCode
            }
            rateDuration
          }
          maxPayRate {
            rate {
              amount
              currencyCode
            }
            rateDuration
          }
          segmentNamePlural
          segmentNamePluralLowerCase
          segmentNameSingular
          segmentNameSingularLowerCase
          avgReviewRating
          smbHeadline
          internalLinkingHeadline
        }
      }
      ... on SEOSegmentationDetailsError {
        __typename
        errors {
          __typename
          message
        }
      }
    }
    seoSegmentationHeroImage(seoSegmentId: $seoSegmentId) {
      __typename
      ... on SEOSegmentationHeroImageSuccess {
        heroImage {
          largeImageURL
          smallImageURL
        }
      }
      ... on SEOSegmentationHeroImageError {
        errors {
          message
          __typename
        }
      }
    }
    seoSegmentationBreadcrumbs(seoSegmentId: $seoSegmentId) {
      ... on SEOSegmentationBreadcrumbsSuccess {
        breadcrumbs {
          content
          url
        }
      }
      ... on SEOSegmentationBreadcrumbsError {
        errors {
          message
        }
      }
    }
    getSEOSegmentationIntro(seoSegmentId: $seoSegmentId) @skip(if: $local) {
      title
      description
    }
    seoSegmentationStats(seoSegmentId: $seoSegmentId) @skip(if: $isJobSegment) {
      ... on SEOSegmentationStatsSuccess {
        caregiverStats {
          title
          totalProviders
          avgYearsOfExperience
          totalReviewCount
          avgReviewRating
          avgPayRate {
            amount
            currencyCode
          }
          jobCount
        }
      }
      ... on SEOSegmentationStatsError {
        errors {
          __typename
          message
        }
      }
    }
    getSEOSegmentationFAQs(seoSegmentId: $seoSegmentId) {
      title
      faqList {
        question
        answer
      }
    }
    getSEOSegmentationLinkSections(seoSegmentId: $seoSegmentId) {
      id
      linkList {
        content
        createDate
        description
        url
        imageDetails {
          imageHeight
          imageSize
          imageUrl
          imageWidth
        }
      }
      title
    }
    seoArticlesList(seoSegmentId: $seoSegmentId) {
      ... on SEOArticlesListSuccess {
        __typename
        seoArticlesList {
          articles {
            createDate
            description
            largeImage {
              imageHeight
              imageSize
              imageUrl
              imageWidth
            }
            smallImage {
              imageHeight
              imageSize
              imageUrl
              imageWidth
            }
            title
            url
          }
        }
      }
      ... on SEOArticlesListError {
        __typename
        errors {
          message
        }
      }
    }
    seoNavigationLinkSection(locationId: $locationId) @include(if: $local) {
      ... on SEONavigationLinkSectionSuccess {
        __typename
        seoNavigationLinkSection {
          childCareLinks {
            title
            urlPath
          }
          housekeepingLinks {
            title
            urlPath
          }
          jobsLinks {
            title
            urlPath
          }
          petCareLinks {
            title
            urlPath
          }
          seniorCareLinks {
            title
            urlPath
          }
          tutoringLinks {
            title
            urlPath
          }
        }
      }
      ... on SEONavigationLinkSectionError {
        __typename
        errors {
          message
        }
      }
    }
  }
`;

type SEOSegmentationListingPageQuery = (
  client: ApolloClient<NormalizedCacheObject>,
  variables: seoSegmentationListingPageVariables
) => Promise<
  | {
      queryResponse: ApolloQueryResult<SeoSegmentationListingPageType>;
      error: null;
    }
  | {
      queryResponse: null;
      error: ApolloError;
    }
>;

export const seoSegmentationListingPage: SEOSegmentationListingPageQuery = async (
  client,
  variables
) => {
  return apolloClientQuery(client, SEO_SEGMENTATION_LISTING_PAGE, variables);
};
